import React, { useState, useCallback, useEffect } from 'react';
import { Grid, Box, Paper, TableContainer, LinearProgress, Typography, Drawer, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { Header } from "../../../components/global/Header";
import FormListToolbar from "../../../components/global/Toolbar";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useNavigate } from "react-router";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import SearchField from '../../../components/search/Search';
import { useExpenses } from './useExpenses';
import ErrorNotification from '../../../components/ErrorNotification/ErrorNotification';
import api from "../../../services/api";
import { useNotifier } from "muinotify";
import { exportToExcel } from '../../../components/export/exportToExcel';
import { exportToPdf } from '../../../components/export/exportToPdf';
import { exportToCsv } from '../../../components/export/exportToCsv';
import ExpensesDetails from './ExpensesDetails';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckIcon from '@mui/icons-material/Check';
import ProductionEdit from './/update';
import ExpenseDetailsEdit from '..//expense_details/update';
import useSWR from 'swr';

const Expenses = () => {
  const { notifier } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduction, setSelectedProduction] = useState(null);
  const { expense, isLoading, error, mutate } = useExpenses(searchQuery, selectedProduction?.id);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedExpenses, setSelectedExpenses] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editExpenseId, setEditExpenseId] = useState(null);
  const NAV_URL = `expenses`;
  const URL = `expense`;
  let navigate = useNavigate();

  const { data: productions } = useSWR('/productions');

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  function getRowId(params) {
    return params.row.id;
  }

  useEffect(() => {
    if (productions && productions.length > 0) {
      const savedProduction = localStorage.getItem('selectedProduction');
      if (savedProduction) {
        try {
          const parsedProduction = JSON.parse(savedProduction);
          const production = productions.find(p => p.id === parsedProduction.id);
          if (production) {
            setSelectedProduction(production);
          }
        } catch (e) {
          // Invalid saved production, ignore
        }
      }
    }
  }, [productions]);

  useEffect(() => {
    if (selectedProduction) {
      localStorage.setItem('selectedProduction', JSON.stringify(selectedProduction));
    }
  }, [selectedProduction]);

  const handleProductionChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      setSelectedProduction(null);
    } else {
      const production = productions?.find((p) => p.id === value);
      setSelectedProduction(production || null);
    }
    mutate();
  };

  const handleClear = () => {
    setSearchQuery("");
    mutate(); // Refetch data after clearing search
  };

  const handleEdit = (id) => {
    setEditId(id);
    setIsDrawerOpen(true);
  };

  const handleEditExpenseEpisode = (id) => {
    setEditExpenseId(id);
    setIsDrawerOpen(true);
  };

  const handlePanelClose = () => {
    setSelectedExpenses(null);
    setIsDrawerOpen(false);
  };

  const handlePanelCloseUpdate = () => {
    setEditId(null);
    setEditExpenseId(null);
    setIsDrawerOpen(false);
    mutate();
  };

  const handleAdd = () => {
    navigate(`/${NAV_URL}/create/`);
  };

  const handleDelete = useCallback((id) => {
    confirmAlert({
      title: 'Επιβεβαίωση διαγραφής',
      message: 'Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτήν την εγγραφή;',
      buttons: [
        {
          label: 'Ναι',
          onClick: async () => {
            try {
              await api.delete(`/${URL}/${id}`);
              notifier("Η εγγραφή διαγράφηκε με επιτυχία.", { variant: "success" });
              await mutate();
            } catch (err) {
              notifier("Αποτυχία διαγραφής εγγραφής. Error: " + err.message, { variant: "error" });
            }
          }
        },
        {
          label: 'Όχι',
        }
      ]
    });
  }, [mutate, notifier]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const flattenExpense = Array.isArray(expense)
    ? expense.map((item) => ({
      ...item,
      Prediction: item.exp_pred ? true : null,
      prod_descr: item.prod_descr?.p_descr || '',
      pci_descr: item.pci_descr?.pci_descr || '',
      expense_beneficiaries_descr: item.expense_beneficiaries_descr?.eb_company_name || '',
      exp_assigned_episodes: Array.isArray(item.exp_assigned_episodes) ? item.exp_assigned_episodes.join(', ') : '',
      formattedExpAmount: formatCurrency(item?.exp_amount),
    })) : [];

  const columnsToExtract = [
    { field: 'Prediction', headerName: 'Πρόβλεψη' },
    { field: 'prod_descr', headerName: 'Παραγωγή' },
    { field: 'pci_descr', headerName: 'Στοιχείο' },
    { field: 'exp_inv_no', headerName: 'Αρ. Τιμολογίου' },
    { field: 'expense_beneficiaries_descr', headerName: 'Συναλλασσόμενος' },
    { field: 'formattedExpAmount', headerName: 'Σύνολο' },
    { field: 'exp_assigned_episodes', headerName: 'Επεισόδια' },
  ];

  const handleExportToExcel = () => {
    exportToExcel(
      flattenExpense,
      columnsToExtract,
      `Έξοδα`
    );
  };

  const handleExportToPdf = () => {
    exportToPdf(
      flattenExpense,
      columnsToExtract,
      `Έξοδα`
    );
  };

  const handleExportToCsv = () => {
    exportToCsv(
      flattenExpense,
      columnsToExtract,
      `Έξοδα`
    );
  };

  const columns = [
    {
      field: 'id',
      headerName: 'id',
      headerAlign: 'center',
      flex: 1,
      minWidth: 100,
      maxWidth: 300,
      sortable: true,
    },
    {
      field: 'Prediction',
      headerName: 'Πρόβλεψη',
      headerAlign: 'center',
      flex: 1,
      minWidth: 80,
      maxWidth: 100,
      renderCell: (params) => (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '100%',
          height: '100%',
        }}>
          {params.value ? <CheckIcon /> : null}
        </div>
      ),
    },
    {
      field: 'prod_descr',
      headerName: 'Παραγωγή',
      headerAlign: 'center',
      flex: 1,
      minWidth: 100,
      maxWidth: 300,
      renderCell: (params) => (
        <div style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '100%'
        }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'pci_descr',
      headerName: 'Στοιχείο',
      headerAlign: 'center',
      flex: 1,
      minWidth: 100,
      maxWidth: 300,
      renderCell: (params) => (
        <div style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '100%'
        }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'exp_inv_no',
      headerName: 'Αρ. Τιμολογίου',
      align: 'center',
      headerAlign: 'center',
      flex: 1,
      minWidth: 100,
      maxWidth: 300,
      renderCell: (params) => (
        <div style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '100%'
        }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'expense_beneficiaries_descr',
      headerName: 'Συναλλασσόμενος',
      headerAlign: 'center',
      flex: 1,
      minWidth: 100,
      maxWidth: 200,
      renderCell: (params) => (
        <div style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '100%'
        }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'exp_amount',
      headerName: 'Σύνολο',
      headerAlign: 'center',
      align: 'right',
      flex: 1,
      minWidth: 80,
      maxWidth: 110,
      renderCell: (params) => (
        <div> {params.row.formattedExpAmount} </div>
      ),
    },
    {
      field: 'exp_original_filename',
      headerName: 'Αρχείο',
      headerAlign: 'center',
      flex: 1,
      minWidth: 50,
      maxWidth: 100,
      renderCell: (params) => {
        const fileUrl = params.row.exp_file
          ? `${process.env.REACT_APP_PROTOCOL === 'https' || !process.env.REACT_APP_BACKEND_PORT ? '' : `http://${process.env.REACT_APP_HOST}:${process.env.REACT_APP_BACKEND_PORT}`}/${URL}/uploads/${params.row.exp_file}`
          : null;

        const handleFileClick = async (event) => {
          event.stopPropagation();

          if (fileUrl) {
            try {
              // File downloads are public (no token required)
              // Use direct fetch instead of api service
              const baseUrl = process.env.REACT_APP_PROTOCOL === 'https' || !process.env.REACT_APP_BACKEND_PORT
                ? '/api'  // Relative URL for production
                : `http://${process.env.REACT_APP_HOST}:${process.env.REACT_APP_BACKEND_PORT}`;
              
              const response = await fetch(`${baseUrl}/expense/uploads/${params.row.exp_file}`, {
                method: 'GET',
              });

              if (response.ok) {
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = params.row.exp_original_filename;
                link.click();
                // Clean up the object URL after a delay
                setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 100);
              } else {
                const errorData = await response.json().catch(() => ({ message: response.statusText }));
                alert(`Error downloading file: ${errorData.message || response.statusText}`);
              }
            } catch (error) {
              console.error("Error fetching file:", error);
              alert(`Error fetching file: ${error.message || 'Unknown error'}`);
            }
          }
        };

        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%'
            }}
          >
            {fileUrl && (
              <a href="#" onClick={handleFileClick}>
                <AttachFileIcon color="primary" />
              </a>
            )}
          </div>
        );
      },
    },
    {
      field: 'exp_assigned_episodes',
      headerName: 'Επεισόδια',
      headerAlign: 'center',
      flex: 1,
      minWidth: 100,
      maxWidth: 300,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%'
          }}>
          <GridActionsCellItem
            color="primary"
            icon={<EditOutlinedIcon />}
            label="Edit"
            onClick={(event) => {
              event.stopPropagation();
              handleEditExpenseEpisode(params.row.id);
            }}
          />
          {params.row.exp_assigned_episodes}
        </div>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerAlign: 'center',
      minWidth: 50,
      maxWidth: 100,
      headerName: "Actions",
      getActions: (params) => [
        <GridActionsCellItem
          color="primary"
          icon={<EditOutlinedIcon />}
          label="Edit"
          onClick={() => handleEdit(params.row.id)}
        />,
        <GridActionsCellItem color="secondary" icon={<DeleteOutlineOutlinedIcon />} label="Delete" onClick={() => handleDelete(params.row.id)} />,
      ],
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Header title="Expenses" module="Expenses" subtitle="List of expenses" />
      </Box>
      
      {/* Production Filter */}
      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel id="prod-label">Παραγωγή</InputLabel>
          <Select
            labelId="prod-label"
            id="prod-id"
            label="Παραγωγή"
            value={selectedProduction?.id || ''}
            onChange={handleProductionChange}
          >
            <MenuItem value="">
              <em>Όλες οι Παραγωγές</em>
            </MenuItem>
            {productions?.map((production) => (
              <MenuItem key={production.id} value={production.id}>
                {production.p_descr}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={selectedExpenses ? 8 : 12}>

          <FormListToolbar
            handleAdd={handleAdd}
            handleExportToExcel={handleExportToExcel}
            handleExportToPdf={handleExportToPdf}
            handleExportToCsv={handleExportToCsv}
          />
          <Box
            sx={{
              mt: 2,
              height: { xs: '70vh', md: '80vh' },
            }}
          >
            <Paper 
              elevation={2} 
              sx={{ 
                padding: 2, 
                height: '100%', 
                width: '100%',
                borderRadius: 2,
                boxShadow: (theme) => theme.shadows[3],
              }}
            >
              <SearchField searchQuery={searchQuery} handleSearchChange={handleSearchChange} handleClear={handleClear} ml={2} mr={2} />

              {isLoading && <LinearProgress />}

              {error && <ErrorNotification ml={2} mr={2} error={error} />}

              <TableContainer style={{ height: '87%', width: '100%' }}>
                <DataGrid
                  rows={flattenExpense || []}
                  columns={columns}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'id', sort: 'asc' }],
                    },
                  }}
                  getRowId={(row) => row.id}
                  columnVisibilityModel={{
                    id: false,
                  }}
                  onRowClick={(params) => setSelectedExpenses(params.row)}
                  sx={{
                    '& .MuiDataGrid-cell': {
                      borderRight: '1px solid #ddd',
                      borderBottom: 'none',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      borderRight: '1px solid #ddd',
                    },
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnsContainer': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '1px solid #e0e0e0',
                    },
                    '& .MuiTablePagination-root': {
                      color: '#000',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 'bold',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                  }}
                />
              </TableContainer>
            </Paper>
          </Box>
        </Grid>

        {selectedExpenses && (
          <Grid item xs={4}>
            <ExpensesDetails
              expense={selectedExpenses}
              onClose={handlePanelClose} />
          </Grid>
        )}

        <Drawer
          anchor="right"
          open={isDrawerOpen}
          onClose={handlePanelClose}
        >
          {editId && <ProductionEdit
            id={editId}
            onClose={handlePanelCloseUpdate}
          />}

          {editExpenseId && <ExpenseDetailsEdit
            id={editExpenseId}
            onClose={handlePanelCloseUpdate}
          />}
        </Drawer>

      </Grid>
    </Box>

  );
};

export default Expenses;